// Personal Drive (My Files) — shared strings, EN + TR.
// Used by the cross-shell PersonalDrivePage (seller / supply / admin).

import type { LocalizedDict } from '../index'

type DrivePersonalStrings = {
  pageTitle: string
  subtitle: string
  uploadFiles: string
  newFolder: string
  sectionPersonal: string
  sectionOperational: string
  autoTag: string
  starred: string
  trash: string
  capacityUnlimited: string
  capacitySoon: string
  capacityUsed: string
  searchPlaceholder: string
  emptyFolder: string
  emptyFolderHint: string
  emptyTrash: string
  sharedWith: string
  home: string
  newFolderTitle: string
  newFolderPlaceholder: string
  create: string
  cancel: string
  readonlyOrders: string
  readonlyProducts: string
  readonlyWorkorders: string
  readonlyGeneric: string
  loadError: string
  dropToUpload: string
  dropOnFolderHint: string
  fileMoved: string
  download: string
  previewMockNote: string
  previewNoInline: string
}

const en: DrivePersonalStrings = {
  pageTitle: 'My Files',
  subtitle:
    'Upload product images, organize them, share with your team — your operational files live here too.',
  uploadFiles: 'Upload Files',
  newFolder: 'New Folder',
  sectionPersonal: 'Personal',
  sectionOperational: 'Operational',
  autoTag: 'auto',
  starred: 'Starred',
  trash: 'Trash',
  capacityUnlimited: '/ unlimited (V1)',
  capacitySoon: 'Storage plans — coming soon',
  capacityUsed: 'used',
  searchPlaceholder: 'Search files…',
  emptyFolder: 'No files yet',
  emptyFolderHint: 'Drag files here or use Upload above',
  emptyTrash: 'Trash is empty',
  sharedWith: 'shared',
  home: 'Home',
  newFolderTitle: 'New folder name',
  newFolderPlaceholder: 'e.g. Summer Collection',
  create: 'Create',
  cancel: 'Cancel',
  readonlyOrders:
    'These folders are created automatically from orders — labels, invoices and packing slips are placed here by the system. Read-only.',
  readonlyProducts:
    'These folders are bound to product cards — the cover and gallery images live here. Read-only.',
  readonlyWorkorders:
    'These folders are created automatically from work orders — supplier hand-offs and production photos land here. Read-only.',
  readonlyGeneric: 'This folder is managed automatically by the system. Read-only.',
  loadError: 'Could not load this folder.',
  dropToUpload: 'Drop files to upload here',
  dropOnFolderHint: 'Open a folder (or drop onto a folder) to upload files.',
  fileMoved: 'Moved ✓',
  download: 'Download',
  previewMockNote: 'Preview available with the real backend.',
  previewNoInline: 'No inline preview for this file type — download to open it.',
}

const tr: DrivePersonalStrings = {
  pageTitle: 'Dosyalarım',
  subtitle:
    'Ürün görsellerini yükle, klasörle, ekibinle paylaş — operasyon dosyaların da otomatik burada.',
  uploadFiles: 'Dosya Yükle',
  newFolder: 'Yeni Klasör',
  sectionPersonal: 'Kişisel',
  sectionOperational: 'Operasyonel',
  autoTag: 'otomatik',
  starred: 'Yıldızlı',
  trash: 'Çöp Kutusu',
  capacityUnlimited: '/ sınırsız (V1)',
  capacitySoon: 'Kapasite planları — yakında',
  capacityUsed: 'kullanıldı',
  searchPlaceholder: 'Dosyalarda ara…',
  emptyFolder: 'Henüz dosya yok',
  emptyFolderHint: 'Dosyaları buraya sürükle veya yukarıdan yükle',
  emptyTrash: 'Çöp kutusu boş',
  sharedWith: 'paylaşıldı',
  home: 'Ana',
  newFolderTitle: 'Yeni klasör adı',
  newFolderPlaceholder: 'ör. Yaz Koleksiyonu',
  create: 'Oluştur',
  cancel: 'Vazgeç',
  readonlyOrders:
    'Bu klasörler siparişlerden otomatik oluşur — etiket, fatura ve paketleme fişleri sistem tarafından buraya konur. Salt-okunur.',
  readonlyProducts:
    'Bu klasörler ürün kartlarına bağlıdır — ürünün kapak ve galeri görselleri buradadır. Salt-okunur.',
  readonlyWorkorders:
    'Bu klasörler iş emirlerinden otomatik oluşur — tedarikçi hand-off ve üretim fotoğrafları buraya düşer. Salt-okunur.',
  readonlyGeneric: 'Bu klasör sistem tarafından otomatik yönetilir. Salt-okunur.',
  loadError: 'Bu klasör yüklenemedi.',
  dropToUpload: 'Yüklemek için dosyaları buraya bırak',
  dropOnFolderHint: 'Dosya yüklemek için bir klasör aç ya da bir klasörün üstüne bırak.',
  fileMoved: 'Taşındı ✓',
  download: 'İndir',
  previewMockNote: 'Önizleme gerçek backend ile görüntülenir.',
  previewNoInline: 'Bu dosya türü için önizleme yok — açmak için indir.',
}

export const drivePersonalI18n: LocalizedDict<DrivePersonalStrings> = { en, tr }
